import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i1 from '@angular/router';
import { NavigationEnd, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';

class BodyClassRenderer {
    constructor(_router, _rendererFactory, _route) {
        this._router = _router;
        this._rendererFactory = _rendererFactory;
        this._route = _route;
        this._renderer = _rendererFactory.createRenderer(null, null);
    }
    get renderer() {
        return this._renderer;
    }
    set renderer(val) {
        this._renderer = val;
    }
    destroy() {
        if (this._bodyClassListener) {
            this._bodyClassListener.unsubscribe();
        }
    }
    addClass(cls) {
        this._renderer.addClass(document.body, cls);
    }
    removeClass(cls) {
        this.renderer.removeClass(document.body, cls);
    }
    addBodyClass(cls) {
        this.addClass(this._sanitizeClass(cls));
    }
    removeBodyClass(cls) {
        this.removeClass(this._sanitizeClass(cls));
    }
    init() {
        this._bodyClassListener = this._router
            .events
            .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationStart))
            .subscribe((event) => {
            document.body.className.split(' ')
                .forEach((name) => {
                if (name.match(/^body-/)) {
                    this.removeBodyClass(name);
                }
            });
            this._getBodyClasses(this._route.snapshot.firstChild)
                .forEach((cls) => {
                this.addBodyClass(cls);
            });
        });
    }
    _getBodyClasses(activatedRoute) {
        const bodyClasses = [];
        if (activatedRoute) {
            if (activatedRoute.data && activatedRoute.data.bodyClass) {
                bodyClasses.push(...this._parseBodyClasses(activatedRoute.data.bodyClass));
            }
            if (activatedRoute.firstChild) {
                bodyClasses.push(...this._getBodyClasses(activatedRoute.firstChild));
            }
        }
        return bodyClasses;
    }
    _parseBodyClasses(data) {
        return data.split(/[\s,]/).filter(Boolean);
    }
    _sanitizeClass(cls) {
        if (cls.indexOf('body-') === -1) {
            return `body-${cls}`;
        }
        return cls;
    }
}
BodyClassRenderer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: BodyClassRenderer, deps: [{ token: i1.Router }, { token: i0.RendererFactory2 }, { token: i1.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Injectable });
BodyClassRenderer.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: BodyClassRenderer });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: BodyClassRenderer, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i0.RendererFactory2 }, { type: i1.ActivatedRoute }]; } });

class FsBodyModule {
    constructor(bodyClassRenderer) {
        this.bodyClassRenderer = bodyClassRenderer;
        this.bodyClassRenderer.init();
    }
    static forRoot() {
        return {
            ngModule: FsBodyModule,
            providers: [BodyClassRenderer],
        };
    }
}
FsBodyModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBodyModule, deps: [{ token: BodyClassRenderer }], target: i0.ɵɵFactoryTarget.NgModule });
FsBodyModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBodyModule, imports: [CommonModule] });
FsBodyModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBodyModule, providers: [], imports: [[
            CommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBodyModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    exports: [],
                    entryComponents: [],
                    declarations: [],
                    providers: [],
                }]
        }], ctorParameters: function () { return [{ type: BodyClassRenderer }]; } });

class HtmlClassRenderer {
    constructor(_rendererFactory) {
        this._rendererFactory = _rendererFactory;
        this._renderer = _rendererFactory.createRenderer(null, null);
    }
    get renderer() {
        return this._renderer;
    }
    set renderer(val) {
        this._renderer = val;
    }
    addClass(cls) {
        this._renderer.addClass(document.documentElement, cls);
    }
    removeClass(cls) {
        this.renderer.removeClass(document.documentElement, cls);
    }
}
HtmlClassRenderer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: HtmlClassRenderer, deps: [{ token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable });
HtmlClassRenderer.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: HtmlClassRenderer, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: HtmlClassRenderer, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i0.RendererFactory2 }]; } });

class HtmlRenderer {
    addStyle(styles, options) {
        const id = (options === null || options === void 0 ? void 0 : options.id) ? ` id="${options === null || options === void 0 ? void 0 : options.id}"` : '';
        if (options === null || options === void 0 ? void 0 : options.id) {
            const el = document.querySelector(`#${options === null || options === void 0 ? void 0 : options.id}`);
            if (el) {
                el.innerHTML = styles;
                return;
            }
        }
        document.head.insertAdjacentHTML('beforeend', `<style${id}>${styles}</style>`);
    }
}
HtmlRenderer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: HtmlRenderer, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
HtmlRenderer.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: HtmlRenderer, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: HtmlRenderer, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BodyClassRenderer, FsBodyModule, HtmlClassRenderer, HtmlRenderer };
